// Admin AI Workspace — Master-Admin-only Claude operator console.
// Mounted at /admin/ai-workspace.
//
// Features:
//   • Chat with Claude in admin-tone (forced role=admin via backend route)
//   • Save assistant outputs as ai_suggestions (pending Master Admin approval)
//   • Review / approve / reject / apply suggestions
//   • View AI audit log
//   • Toggle assistant feature flags + default/premium model
import React, { useEffect, useState } from "react";
import {
  Bot, ListChecks, ClipboardCheck, Cog, Loader2, CheckCircle2,
  XCircle, Activity, Send,
} from "lucide-react";
import AssistantPanel from "@/components/assistant/AssistantPanel";
import api from "@/lib/api";

const ALLOWED_MODELS = [
  "claude-sonnet-4-6",
  "claude-opus-4-7",
  "claude-opus-4-8",
  "claude-sonnet-4-5-20250929",
  "claude-haiku-4-5-20251001",
];

const SUGGESTION_TYPES = [
  "copy_draft", "product_update", "order_action", "user_action",
  "payout_action", "square_sync", "content_calendar",
  "affiliate_outreach", "policy_update",
];

export default function AdminAIWorkspace() {
  const [tab, setTab] = useState("chat");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-ai-workspace">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="eyebrow text-lifted-dim">Master Admin</div>
          <h1 className="font-anton text-4xl uppercase text-white">AI Workspace</h1>
          <p className="text-lifted-dim text-sm mt-1 max-w-2xl">
            X helps you draft, audit, and ship across the Lifted ecosystem.
            All write-actions require your explicit approval.
          </p>
        </div>
        <nav className="flex gap-2" data-testid="ai-workspace-tabs">
          {[
            { id: "chat",      label: "Chat",        icon: Bot },
            { id: "queue",     label: "Suggestions", icon: ListChecks },
            { id: "audit",     label: "Audit",       icon: Activity },
            { id: "settings",  label: "Settings",    icon: Cog },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`ai-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-[0.16em] font-bebas border transition-colors
                ${tab === t.id
                  ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                  : "bg-black/30 text-lifted-dim border-white/10 hover:text-white"}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      {tab === "chat" && <ChatTab />}
      {tab === "queue" && <QueueTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "settings" && <SettingsTab />}
    </div>
  );
}

// ---- Chat tab ----------------------------------------------------------
function ChatTab() {
  const [savingOpen, setSavingOpen] = useState(false);
  const [draftType, setDraftType] = useState("copy_draft");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [savedOk, setSavedOk] = useState("");
  const [saveErr, setSaveErr] = useState("");

  async function save() {
    setSaveErr(""); setSavedOk("");
    try {
      const { data } = await api.post("/admin/ai/suggestions", {
        type: draftType,
        title: draftTitle.trim() || "Untitled suggestion",
        body: draftBody.trim(),
      });
      setSavedOk(`Saved as pending suggestion ${data.id.slice(0, 8)}`);
      setSavingOpen(false);
      setDraftTitle(""); setDraftBody("");
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || "Could not save.");
    }
  }

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="ai-chat-tab">
      <div className="lg:col-span-2">
        <AssistantPanel
          endpoint="/admin/ai/workspace/chat"
          surface="admin"
          role="admin"
          title="X — Master Admin"
          subtitle="Operator mode · suggestions require approval"
          testid="admin-assistant-panel"
        />
      </div>
      <aside className="glass-panel p-5 space-y-3" data-testid="ai-quick-save">
        <h3 className="font-anton uppercase text-white text-lg flex items-center gap-2">
          <ClipboardCheck size={14} className="text-lifted-green" />
          Save assistant output
        </h3>
        <p className="text-xs text-lifted-dim leading-relaxed">
          Paste the assistant's draft below to queue it as a pending
          suggestion. Suggestions never run automatically — you must explicitly
          approve and apply.
        </p>
        {!savingOpen ? (
          <button
            type="button"
            onClick={() => setSavingOpen(true)}
            className="btn-lifted w-full"
            data-testid="ai-open-save"
          >
            New suggestion
          </button>
        ) : (
          <div className="space-y-2">
            <select
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white"
              data-testid="ai-save-type"
            >
              {SUGGESTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="Short title…"
              className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white placeholder:text-lifted-dim"
              data-testid="ai-save-title"
              maxLength={200}
            />
            <textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              placeholder="Paste the draft / proposed change…"
              rows={7}
              className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white placeholder:text-lifted-dim"
              data-testid="ai-save-body"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={save}
                disabled={!draftBody.trim()}
                className="btn-lifted flex-1 disabled:opacity-50"
                data-testid="ai-save-submit"
              >
                <Send size={12} className="inline mr-1" /> Save as pending
              </button>
              <button
                type="button"
                onClick={() => setSavingOpen(false)}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {savedOk && (
          <div className="text-xs text-lifted-green flex items-center gap-1.5"
               data-testid="ai-save-ok">
            <CheckCircle2 size={12} /> {savedOk}
          </div>
        )}
        {saveErr && (
          <div className="text-xs text-rose-200 flex items-center gap-1.5"
               data-testid="ai-save-err">
            <XCircle size={12} /> {saveErr}
          </div>
        )}
      </aside>
    </div>
  );
}

// ---- Queue tab ---------------------------------------------------------
function QueueTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/ai/suggestions?status=${filter}&limit=100`);
      setItems(data?.suggestions || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function act(id, action) {
    setBusyId(id);
    try {
      await api.post(`/admin/ai/suggestions/${id}/${action}`);
      await load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || `Could not ${action}.`);
    } finally { setBusyId(""); }
  }

  return (
    <div className="space-y-4" data-testid="ai-queue-tab">
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "applied"].map(s => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            data-testid={`ai-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bebas border
              ${filter === s
                ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                : "bg-black/30 text-lifted-dim border-white/10 hover:text-white"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-lifted-dim" data-testid="ai-queue-empty">
          No {filter} suggestions yet.
        </div>
      )}

      <ul className="space-y-3">
        {items.map(s => (
          <li key={s.id} className="glass-panel p-4" data-testid={`ai-suggestion-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.16em] font-bebas px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-lifted-dim">
                    {s.type}
                  </span>
                  <span className="text-white font-bebas uppercase text-sm">{s.title}</span>
                </div>
                <pre className="text-xs text-lifted-dim mt-2 whitespace-pre-wrap font-mono max-h-40 overflow-auto">
                  {s.body}
                </pre>
                <div className="text-[10px] text-lifted-dim font-mono mt-1">
                  id {s.id.slice(0, 8)} · created {s.created_at?.slice(0, 16)}
                </div>
              </div>
              {filter === "pending" && (
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button onClick={() => act(s.id, "approve")} disabled={busyId === s.id}
                          className="btn-lifted text-[10px]" data-testid={`approve-${s.id}`}>
                    Approve
                  </button>
                  <button onClick={() => act(s.id, "reject")} disabled={busyId === s.id}
                          className="btn-ghost text-[10px]" data-testid={`reject-${s.id}`}>
                    Reject
                  </button>
                </div>
              )}
              {filter === "approved" && (
                <button onClick={() => act(s.id, "apply")} disabled={busyId === s.id}
                        className="btn-lifted text-[10px]" data-testid={`apply-${s.id}`}>
                  Mark Applied
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---- Audit tab ---------------------------------------------------------
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/ai/audit-logs?limit=200");
        setRows(data?.logs || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-3" data-testid="ai-audit-tab">
      {loading && <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-sm text-lifted-dim">No AI audit entries yet.</div>}
      {rows.length > 0 && (
        <table className="w-full text-xs" data-testid="ai-audit-table">
          <thead className="text-lifted-dim uppercase tracking-[0.16em] font-bebas">
            <tr><th className="text-left px-2 py-1.5">When</th>
                <th className="text-left px-2 py-1.5">Actor</th>
                <th className="text-left px-2 py-1.5">Action</th>
                <th className="text-left px-2 py-1.5">Target</th>
                <th className="text-left px-2 py-1.5">Meta</th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r, i) => (
              <tr key={i} className="text-white">
                <td className="px-2 py-1.5 font-mono text-[10px]">{(r.created_at||"").slice(0,19)}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{(r.actor||"").slice(0,8)}</td>
                <td className="px-2 py-1.5">{r.action}</td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{(r.target_id||"—").slice(0,12)}</td>
                <td className="px-2 py-1.5 text-lifted-dim text-[10px] font-mono">
                  {r.meta ? JSON.stringify(r.meta).slice(0, 80) : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- Settings tab ------------------------------------------------------
function SettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/ai/settings"); setS(data); }
      catch { setS(null); }
    })();
  }, []);

  async function save() {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.post("/admin/ai/settings", s);
      setS(data); setMsg("Saved.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  }
  if (!s) return <div className="text-sm text-lifted-dim flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="glass-panel p-5 space-y-4 max-w-2xl" data-testid="ai-settings-tab">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Default model" testid="ai-setting-default-model">
          <select value={s.default_model} onChange={(e) => setS({ ...s, default_model: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white">
            {ALLOWED_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Premium model" testid="ai-setting-premium-model">
          <select value={s.premium_model} onChange={(e) => setS({ ...s, premium_model: e.target.value })}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-sm text-white">
            {ALLOWED_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
      </div>
      <Toggle label="Assistant master switch" v={s.assistant_enabled}
              onChange={(v) => setS({ ...s, assistant_enabled: v })} testid="ai-toggle-master" />
      <Toggle label="Public (unauthenticated) assistant" v={s.public_assistant_enabled}
              onChange={(v) => setS({ ...s, public_assistant_enabled: v })} testid="ai-toggle-public" />
      <Toggle label="Affiliate assistant" v={s.affiliate_assistant_enabled}
              onChange={(v) => setS({ ...s, affiliate_assistant_enabled: v })} testid="ai-toggle-aff" />
      <Toggle label="Admin workspace" v={s.admin_workspace_enabled}
              onChange={(v) => setS({ ...s, admin_workspace_enabled: v })} testid="ai-toggle-admin" />
      <div className="flex gap-2 items-center">
        <button type="button" onClick={save} disabled={saving} className="btn-lifted" data-testid="ai-settings-save">
          {saving ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
          Save settings
        </button>
        {msg && <span className="text-xs text-lifted-dim">{msg}</span>}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block text-xs text-lifted-dim font-bebas uppercase tracking-[0.18em]" data-testid={testid}>
      {label}
      <div className="mt-1">{children}</div>
    </label>
  );
}
function Toggle({ label, v, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm text-white" data-testid={testid}>
      <span>{label}</span>
      <button
        type="button"
        onClick={() => onChange(!v)}
        className={`relative w-10 h-5 rounded-full transition-colors ${v ? "bg-lifted-green" : "bg-white/15"}`}
        aria-pressed={v}
      >
        <span className={`absolute top-0.5 ${v ? "right-0.5" : "left-0.5"} w-4 h-4 bg-white rounded-full transition-all`} />
      </button>
    </label>
  );
}
